import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Navbar } from "@/components/site/Navbar";
import { Footer } from "@/components/site/Footer";
import { PropertyCard } from "@/components/site/PropertyCard";
import { InquiryForm } from "@/components/site/InquiryForm";
import { ArrowRight, ShieldCheck, House, GraduationCap, Briefcase } from "@phosphor-icons/react";

const HERO_IMG = "https://media.rightmove.co.uk/property-photo/140d4f415/174043892/140d4f41525753d002402d80cf5bf8bc.jpeg";

export default function HomePage() {
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/properties");
        setProperties(data);
      } catch (e) {
        // ignore
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  return (
    <div className="App">
      <Navbar />

      {/* HERO */}
      <section className="border-b border-[var(--border-subtle)]" data-testid="hero-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-16 md:py-24 grid grid-cols-12 gap-8 items-center">
          <div className="col-span-12 md:col-span-5 fade-in-up">
            <div className="label-uppercase">Hull · East Yorkshire</div>
            <h1 className="h1 text-5xl md:text-6xl lg:text-7xl mt-4">
              <span className="text-[var(--brand)]">Premium lets in Hull —</span>
              <br />
              <span className="text-[var(--accent)]">designed for living.</span>
            </h1>
            <p className="mt-6 text-lg text-[var(--text-secondary)] max-w-xl">
              Carefully curated rental homes from an independent local landlord. Designed for working
              professionals and students who want a place to call home.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link to="/properties" className="btn-primary" data-testid="hero-view-properties-button">
                View properties <ArrowRight size={18} weight="bold" />
              </Link>
              <Link to="/contact" className="btn-outline" data-testid="hero-contact-button">
                Arrange a viewing
              </Link>
            </div>
            <div className="mt-10 flex flex-wrap gap-6 text-sm text-[var(--text-tertiary)]">
              <span className="flex items-center gap-2"><ShieldCheck size={16} /> Deposit-protected</span>
              <span className="flex items-center gap-2"><House size={16} /> Local landlord</span>
              <span className="flex items-center gap-2"><GraduationCap size={16} /> Students welcome</span>
            </div>
          </div>

          <div className="col-span-12 md:col-span-7 fade-in-up" style={{ animationDelay: '120ms' }}>
            <div className="relative aspect-[4/3] md:aspect-[5/4] w-full overflow-hidden">
              <img src={HERO_IMG} alt="Hull property" className="w-full h-full object-cover" />
              <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/60 to-transparent p-6">
                <div className="text-white">
                  <div className="label-uppercase !text-white/70">Featured</div>
                  <div className="font-display text-2xl md:text-3xl mt-1">Ventnor Street · Walgrave Street · Hull HU5</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* PROPERTIES */}
      <section className="max-w-7xl mx-auto px-6 md:px-12 py-20" data-testid="properties-section">
        <div className="flex items-end justify-between mb-10">
          <div>
            <div className="label-uppercase">Our portfolio</div>
            <h2 className="h2 text-4xl md:text-5xl mt-3 max-w-2xl">Three homes in HU5.<br/>One careful landlord.</h2>
          </div>
          <Link to="/properties" className="hidden md:inline-flex items-center gap-1 link-inline" data-testid="see-all-properties-link">
            See all <ArrowRight size={14} />
          </Link>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8" data-testid="properties-grid">
          {loading ? (
            Array.from({ length: 3 }).map((_, i) => (
              <div key={i} className="aspect-[4/3] bg-[var(--bg-secondary)] animate-pulse" />
            ))
          ) : (
            properties.map((p, idx) => <PropertyCard key={p.id} property={p} index={idx} />)
          )}
        </div>
      </section>

      {/* WHO WE LET TO */}
      <section className="bg-[var(--bg-secondary)] border-y border-[var(--border-subtle)]" data-testid="audience-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-20 grid grid-cols-1 md:grid-cols-2 gap-10">
          <div>
            <div className="label-uppercase">Who we let to</div>
            <h2 className="h2 text-4xl md:text-5xl mt-3">Built for the people of Hull.</h2>
            <p className="mt-6 text-lg text-[var(--text-secondary)] max-w-xl">
              From new starters at the docks to final-year students on Cottingham Road, our homes are
              chosen, refurbished, and managed with the people who'll actually live in them in mind.
            </p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
            <div className="bg-white border border-[var(--border-subtle)] p-6">
              <Briefcase size={28} weight="regular" />
              <h3 className="h3 text-xl mt-4">Professionals</h3>
              <p className="text-sm text-[var(--text-secondary)] mt-2">
                Quiet, well-finished homes with fast broadband and easy commutes to the city centre and Hull Royal Infirmary.
              </p>
            </div>
            <div className="bg-white border border-[var(--border-subtle)] p-6">
              <GraduationCap size={28} weight="regular" />
              <h3 className="h3 text-xl mt-4">Students</h3>
              <p className="text-sm text-[var(--text-secondary)] mt-2">
                Walk-to-campus housing with optional bills-inclusive packages. Sign by group, settle in by September.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA / Inquiry */}
      <section className="max-w-7xl mx-auto px-6 md:px-12 py-20 grid grid-cols-1 lg:grid-cols-12 gap-12" data-testid="home-inquiry-section">
        <div className="lg:col-span-6">
          <div className="label-uppercase">Get in touch</div>
          <h2 className="h2 text-4xl md:text-5xl mt-3">Found one you like?</h2>
          <p className="mt-6 text-lg text-[var(--text-secondary)] max-w-lg">
            Send us a quick message and we'll come back to you within 24 hours with viewing slots,
            availability and any extra detail you need.
          </p>
          <div className="mt-8 border-t border-[var(--border-subtle)] pt-6 space-y-3 text-sm text-[var(--text-secondary)]">
            <div><span className="label-uppercase mr-3">Email</span> ampmpropertyinvestments@gmail.com</div>
            <div><span className="label-uppercase mr-3">Phone</span> <a href="tel:+447365813254" className="link-inline">07365 813254</a></div>
            <div><span className="label-uppercase mr-3">Area</span> Hull, East Yorkshire</div>
          </div>
        </div>
        <div className="lg:col-span-6 bg-white border border-[var(--border-subtle)] p-8">
          <InquiryForm />
        </div>
      </section>

      <Footer />
    </div>
  );
}
